import { useEffect, useCallback, useRef } from "react";
import { CONSTANTS } from "../data";
import { generateCustomerOrder } from "../utils/customerUtils";
import type { GameState } from "../types";

// Split: customer spawning, patience updates, order generation

const useCustomerSpawning = ({ state, dispatch }: { state: GameState; dispatch: any }) => {
  const spawnCustomer = useCallback(() => {
    // Calculate max queue size based on rush mode
    const maxQueueSize =
      state.sessionMode.type === "rush"
        ? Math.floor(CONSTANTS.MAX_QUEUE_SIZE * (CONSTANTS.RUSH_MODE as any).maxQueueMultiplier)
        : CONSTANTS.MAX_QUEUE_SIZE;

    if (state.customers.filter((c) => c.active).length >= maxQueueSize) {
      return;
    }

    const isKid = Math.random() < CONSTANTS.KID_SPAWN_CHANCE;
    const customerType = isKid ? "kid" : "regular";
    const patienceRange = CONSTANTS.CUSTOMER_PATIENCE[customerType];

    // Calculate base patience
    let patience = Math.floor(Math.random() * (patienceRange.max - patienceRange.min + 1)) + patienceRange.min;

    // Adjust patience for rush mode
    if (state.sessionMode.type === "rush") {
      patience = Math.floor(patience * (CONSTANTS.RUSH_MODE as any).patienceMultiplier);
    }

    const name = CONSTANTS.CUSTOMER_NAMES[Math.floor(Math.random() * CONSTANTS.CUSTOMER_NAMES.length)];

    let preferenceTag: string | undefined = undefined;
    if (Math.random() < CONSTANTS.CUSTOMER_PREFERENCES.CHANCE) {
      preferenceTag = CONSTANTS.CUSTOMER_PREFERENCES.TAGS[Math.floor(Math.random() * CONSTANTS.CUSTOMER_PREFERENCES.TAGS.length)];
    }

    const order = generateCustomerOrder(state, preferenceTag);

    const customer = {
      id: `customer_${state.customerIdCounter}`,
      name,
      order,
      patience,
      maxPatience: patience, // Store original patience for UI
      type: customerType,
      active: true,
      preferenceTag,
      preferenceBonus: CONSTANTS.CUSTOMER_PREFERENCES.TIP_BONUS,
    };

    dispatch({ type: "ADD_CUSTOMER", customer });
  }, [state, dispatch]);

  const savedCallback = useRef(spawnCustomer);

  useEffect(() => {
    savedCallback.current = spawnCustomer;
  }, [spawnCustomer]);

  useEffect(() => {
    if (state.screen !== "game") return;

    const { baseRate, minRate, levelModifier } = CONSTANTS.CUSTOMER_SPAWN_CONFIG;
    let spawnRate = Math.max(minRate, baseRate - (state.level - 1) * levelModifier);

    // Adjust spawn rate for rush mode
    if (state.sessionMode.type === "rush") {
      spawnRate = Math.floor(spawnRate / (CONSTANTS.RUSH_MODE as any).spawnRateMultiplier);
    }

    const intervalId = setInterval(() => {
      savedCallback.current();
    }, spawnRate);

    return () => clearInterval(intervalId);
  }, [state.screen, state.level, state.sessionMode.type]);
};

const useCustomerPatience = ({ state, dispatch }: { state: GameState; dispatch: any }) => {
  useEffect(() => {
    if (state.screen !== "game") return;

    const patienceInterval = setInterval(() => {
      dispatch({ type: "UPDATE_ALL_CUSTOMER_PATIENCE" });
    }, 1000);

    return () => clearInterval(patienceInterval);
  }, [state.screen, dispatch]);
};

const useCustomerManagement = (props: { state: GameState; dispatch: any }) => {
  useCustomerSpawning(props);
  useCustomerPatience(props);
};

export default useCustomerManagement;
